import React, { FC, Fragment } from 'react';
import styles from './infos.module.css';

// Helpers
import {
  getInfosDateFormated,
  getMonthShortText,
} from '../../helpers/date-helper';
import { getMealFlag } from '../../helpers/string-format-helper';
import { getAbsenceReasonLabel } from '../../helpers/absence-helper';

// Models
import { DiaryType } from '../../models/diary-models';
import SubmitButton from '../submit-button/submit-button';

const Infos: FC<InfosParams> = ({
  title,
  titleColor,
  backgroundColor,
  data,
}) => {
  let backgroundColorClass;
  switch (backgroundColor) {
    case 'gray':
      backgroundColorClass = styles.backgroundGray;
      break;
    case 'white':
      backgroundColorClass = styles.backgroundWhite;
      break;
    default:
      backgroundColorClass = styles.backgroundWhite;
  }

  let titleColorClass;
  switch (titleColor) {
    case 'pencil':
      titleColorClass = styles.titleColorPencil;
      break;
    case 'curiosity':
      titleColorClass = styles.titleColorCuriosity;
      break;
    case 'intermediate':
      titleColorClass = styles.titleColorIntermediate;
      break;
    default:
      titleColorClass = styles.titleColorPencil;
  }

  const hasAnyMeal = (diaryData) =>
    diaryData.breakfast || diaryData.afternoonCoffee || diaryData.lunch || diaryData.dinner;

  const hasAnyChecklist = (diaryData) =>
    !isNullOrUndefined(diaryData.sleep)
    || !isNullOrUndefined(diaryData.diaper)
    || !isNullOrUndefined(diaryData.evacuate)
    || !isNullOrUndefined(diaryData.shower);


  const isNullOrUndefined = (value) => value === null || value === undefined;

  const renderData = () => {
    let items = [];

    if (Array.isArray(data)) {
      items = data.map((item, index) => {
        switch (item.type) {
          case 'title':
            return <h3 key={index}>{item.content}</h3>;
          case 'text':
            return <p key={index}>{item.content}</p>;
          case 'image':
            if (!item.content) return null;

            return (
              <div
                className={styles.image}
                key={index}
                style={{ backgroundImage: `url(${item.content})` }}
              />
            );
          case 'dateWithPlace':
            const { startsAt: startTime, endsAt: endTime, place } = item;

            return (
              <div key={index} className={styles.dateWithPlaceContainer}>
                <div className={styles.dateFull}>
                  <p key={index} className={styles.dateFormated}>
                    {getInfosDateFormated(startTime, endTime)}
                  </p>
                  <p className={styles.place}>{place}</p>
                </div>
                <div className={styles.dateShort}>
                  <p>{getMonthShortText(startTime.getMonth())}</p>
                  <p>{startTime.getDate()}</p>
                </div>
              </div>
            );
          case 'attachments':
            return item.content?.map((attachment, index) => (
              <a
                key={index}
                href={attachment.location}
                target='_blank'
                rel='noopener noreferrer'
                className={styles.attachmentDownload}
                download
              >
                {attachment.originalName}
              </a>
            ));
          case 'imageList':
            return (
              <div key={index} className={styles.imageListContainer}>
                {item.content?.map((image, imgIndex) => (
                  <img
                    key={imgIndex}
                    src={image.location}
                    alt={image.originalName}
                    className={styles.imageListItem}
                  />
                ))}
              </div>
            );
          case 'activityInfo':
            const dueDate = item.dueDate;
            return (
              <div key={index} className={styles.activityInfo}>
                <div className={styles.activityInfoDescription}>
                  <p>{item.description}</p>
                </div>
                <div className={styles.activityInfoDueDate}>
                  <p className={styles.activityInfoDueDateLabel}>
                    data de entrega
                  </p>
                  <div className={styles.dateShort}>
                    <p>{getMonthShortText(dueDate.getMonth())}</p>
                    <p>{dueDate.getDate()}</p>
                  </div>
                </div>
              </div>
            );
          case 'galleryInfo':
            const galleryDate = item.date? new Date(item.date) :  null;
            return (
              <div key={index} className={styles.activityInfo}>
                <div className={styles.activityInfoDescription}>
                  <p>{item.description}</p>
                </div>
                {galleryDate && (
                  <div className={styles.activityInfoDueDate}>
                    <div className={styles.dateShort}>
                      <p>{getMonthShortText(galleryDate.getMonth())}</p>
                      <p>{galleryDate.getDate()}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          default:
            return null;
        }
      });
    } else if (data.type === 'list') {
      return (
        <table className={styles.table}>
          <thead>
            <tr>
              {data.content.titles.map((title, index) => (
                <th key={index} style={title.customStyle}>
                  {title.text}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.content.rows.map((row, index) => {
              return (
                <tr
                  key={index}
                  className={[
                    styles.rowClass,
                    backgroundColor === 'white' ? styles.lightHr : '',
                  ].join(' ')}
                >
                  {row.map((rowData, index) => (
                    <td
                      key={index}
                      style={rowData.customStyle}
                      className={
                        rowData.onClick ? styles.tableItemCursor : null
                      }
                      onClick={rowData.onClick}
                    >
                      {!rowData.icon && <p>{rowData.text}</p>}
                      {rowData.icon && (
                        <img
                          src={rowData.icon}
                          alt={rowData.text}
                          title={rowData.text}
                          className={
                            rowData.onClick ? styles.tableItemCursor : null
                          }
                          onClick={rowData.onClick}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    } else if (data.type === 'absence') {
      const entrieDate = data.entrieDate;
      const absenceDate = data.absenceData.absenceDate;

      return (
        <div className={styles.diaryData}>
          <div className={styles.diaryContainer}>
            <div className={styles.diaryContainerGray}>
              <div className={styles.dateShort}>
                <p>{getMonthShortText(entrieDate.getMonth())}</p>
                <p>{entrieDate.getDate()}</p>
              </div>
              <p>
                <b>Data da falta:</b>{' '}
                {absenceDate.toLocaleDateString('pt-br')}
              </p>
              <p>
                <b>Status:</b>{' '}
                {data.absenceData.isJustified ? 'Justificada' : 'Não justificada'}
              </p>
              {data.absenceData.isJustified && data.absenceData.reason && (
                <p>
                  <b>Motivo:</b>{' '}
                  {getAbsenceReasonLabel(data.absenceData.reason)}
                </p>
              )}
              {data.absenceData.isJustified && data.absenceData.details && (
                <p>
                  <b>Detalhes:</b>{' '}
                  {data.absenceData.details}
                </p>
              )}
            </div>
            {data.absenceData.sickNote && (
              <div className={styles.sickNoteAttachment}>
                <p><b>Atestado:</b></p>
                <a
                  href={data.absenceData.sickNote.downloadUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.attachmentDownload}
                  download
                >
                  {data.absenceData.sickNote.originalName}
                </a>
              </div>
            )}
            <p className={styles.diaryByText}>Por: {data.reporterName}</p>
          </div>
          {data.absenceData.isDeletable && (
            <div className={styles.diaryDelete}>
              <SubmitButton
                value='deletar'
                variant='terciary'
                onClick={() => data.absenceData.deleteAbsenceCallback()}
              />
            </div>
          )}
        </div>
      );
    } else {
      if (data.diaryType === DiaryType.simple) {
        const entrieDate = data.entrieDate;

        return (
          <div className={styles.diaryData}>
            <div className={styles.diaryContainer}>
              <div className={styles.dateShort}>
                <p>{getMonthShortText(entrieDate.getMonth())}</p>
                <p>{entrieDate.getDate()}</p>
              </div>
              <h3>Recados</h3>
              <p className={styles.diaryContainerGray}>
                {data.diaryData.details}
              </p>
              <p className={styles.diaryByText}>Por: {data.reporterName}</p>
            </div>
            {data.diaryData.isDeletable && (
              <div className={styles.diaryDelete}>
                <SubmitButton
                  value='deletar'
                  variant='terciary'
                  onClick={() => data.diaryData.deleteDiaryCallback()}
                />
              </div>
            )}
          </div>
        );
      } else if (data.diaryType === DiaryType.detailed) {
        const getDataTimes = (times: number) => {
          if (times === 1) return `${times} vez`;
          else if (times > 1) return `${times} vezes`;
        };

        const entrieDate = data.entrieDate;

        return (
          <div className={styles.diaryData}>
            <div className={styles.diaryContainer}>
              <div className={styles.diaryContainerGray}>
                <div className={styles.dateShort}>
                  <p>{getMonthShortText(entrieDate.getMonth())}</p>
                  <p>{entrieDate.getDate()}</p>
                </div>
                {hasAnyChecklist(data.diaryData) && (<p>Durante o dia:</p>)}
                {isNullOrUndefined(data.diaryData.sleep) ? null : data.diaryData.sleep === 0 ? (
                  <p>
                    <b>Não</b> dormiu
                  </p>
                ) : (
                  <p>
                    Dormiu <b>{`${getDataTimes(data.diaryData.sleep)}`}</b>
                  </p>
                )}
                {isNullOrUndefined(data.diaryData.evacuate) ? null : data.diaryData.evacuate === 0 ? (
                  <p>
                    <b>Não</b> evacuou
                  </p>
                ) : (
                  <p>
                    Evacuou <b>{`${getDataTimes(data.diaryData.evacuate)}`}</b>
                  </p>
                )}
                {isNullOrUndefined(data.diaryData.shower) ? null : data.diaryData.shower === 0 ? (
                  <p>
                    <b>Não</b> tomou banho
                  </p>
                ) : (
                  <p>
                    Tomou banho{' '}
                    <b>{`${getDataTimes(data.diaryData.shower)}`}</b>
                  </p>
                )}
                {isNullOrUndefined(data.diaryData.diaper) ? null : data.diaryData.diaper === 0 ? (
                  <p>
                    <b>Não</b> trocou de fralda
                  </p>
                ) : (
                  <p>
                    Trocou de fralda{' '}
                    <b>{`${getDataTimes(data.diaryData.diaper)}`}</b>
                  </p>
                )}
                {hasAnyMeal(data.diaryData) && (<p className={styles.diaryContainerTitle}>
                  Na hora das refeições:
                </p>)}
                {data.diaryData.breakfast && (<p>
                  <b>
                    {getMealFlag(data.diaryData.breakfast)}{' '}
                    {data.diaryData.breakfast === 'Half' ? 'do' : 'o'}
                  </b>{' '}
                  café da manhã
                </p>)}
                {data.diaryData.afternoonCoffee && (<p>
                  <b>
                    {getMealFlag(data.diaryData.afternoonCoffee)}{' '}
                    {data.diaryData.afternoonCoffee === 'Half' ? 'do' : 'o'}
                  </b>{' '}
                  lanche da tarde
                </p>)}
                {data.diaryData.lunch && (<p>
                  <b>
                    {getMealFlag(data.diaryData.lunch)}{' '}
                    {data.diaryData.lunch === 'Half' ? 'do' : 'o'}
                  </b>{' '}
                  almoço
                </p>)}
                {data.diaryData.dinner && (<p>
                  <b>
                    {getMealFlag(data.diaryData.dinner)}{' '}
                    {data.diaryData.dinner === 'Half' ? 'do' : 'o'}
                  </b>{' '}
                  jantar
                </p>)}
                {data.diaryData.details && (<Fragment>
                  <p className={styles.diaryContainerTitle}>
                    <b>Além disso:</b>
                  </p>
                  <p>{data.diaryData.details}</p>
                </Fragment>)}
              </div>
              <p className={styles.diaryByText}>Por: {data.reporterName}</p>
            </div>
            {data.diaryData.isDeletable && (
              <div className={styles.diaryDelete}>
                <SubmitButton
                  value='deletar'
                  variant='terciary'
                  onClick={() => data.diaryData.deleteDiaryCallback()}
                />
              </div>
            )}
          </div>
        );
      } else {
        return items;
      }
    }

    return items;
  };

  return (
    <div className={styles.infosContainer}>
      {title && <h2 className={titleColorClass}>{title}</h2>}
      <div
        className={`${styles.contentContainer} ${backgroundColorClass} shadow-box`}
      >
        {renderData()}
      </div>
    </div>
  );
};

export default Infos;
